/**
 * On-The-Fly WebRTC File Transfer Engine (elveris-webrtc)
 *
 * Implements high-performance, memory-efficient peer-to-peer file streaming
 * over WebRTC RTCDataChannel (RFC 8831).
 *
 * Key Capabilities:
 * - On-The-Fly Slicing: Streams directly from disk-backed `File` objects via `file.slice()`.
 *   Peak sender RAM is restricted to a single chunk (~64-256 KiB) rather than buffering
 *   entire multi-gigabyte files in heap.
 * - Raw Binary Wire Framing: Bypasses 33% Base64 stringification overhead and JSON
 *   serialization latency, transmitting raw ArrayBuffers directly over binary DataChannel.
 * - Direct-To-Disk Receiver (0 MB RAM): Uses the File System Access API (`showSaveFilePicker`)
 *   to stream received chunks directly to disk (`FileSystemWritableFileStream`).
 * - Progressive Binary In-Memory Sink: Zero-copy Blob reconstruction fallback for Safari/Firefox.
 * - Adaptive Backpressure Pacing: Actively monitors `dataChannel.bufferedAmount` to prevent
 *   SCTP send buffer exhaustion.
 * - Real-Time Live Telemetry: Computes smoothed throughput (MB/s), live ETA, and chunk metrics.
 */

import { webrtcManager } from './webrtc-native'

export const ON_THE_FLY_MAGIC = 0x57 // 'W' for WebRTC On-The-Fly
export const ON_THE_FLY_FRAME_CHUNK = 0x01
export const FRAME_HEADER_LEN = 26

/**
 * Binary chunk wire frame layout:
 * [0]     : Magic 0x57
 * [1]     : Frame type 0x01 (CHUNK)
 * [2..17] : Transfer ID (16 bytes ASCII, zero-padded)
 * [18..21]: Chunk Index (uint32 BE)
 * [22..25]: Total Chunks (uint32 BE)
 * [26..]  : Raw Binary Chunk Payload
 */
export function encodeChunkFrame(
  transferId: string,
  chunkIndex: number,
  totalChunks: number,
  payload: Uint8Array
): Uint8Array {
  const packet = new Uint8Array(FRAME_HEADER_LEN + payload.length)
  packet[0] = ON_THE_FLY_MAGIC
  packet[1] = ON_THE_FLY_FRAME_CHUNK

  // Write up to 16 bytes of ASCII transfer ID
  const enc = new TextEncoder()
  const idBytes = enc.encode(transferId)
  for (let i = 0; i < 16; i++) {
    packet[2 + i] = i < idBytes.length ? idBytes[i] : 0
  }

  const dv = new DataView(packet.buffer, packet.byteOffset, packet.byteLength)
  dv.setUint32(18, chunkIndex, false)
  dv.setUint32(22, totalChunks, false)

  packet.set(payload, FRAME_HEADER_LEN)
  return packet
}

export function decodeChunkFrame(buffer: ArrayBuffer | Uint8Array): {
  transferId: string
  chunkIndex: number
  totalChunks: number
  payload: Uint8Array
} | null {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  if (bytes.length < FRAME_HEADER_LEN) return null
  if (bytes[0] !== ON_THE_FLY_MAGIC || bytes[1] !== ON_THE_FLY_FRAME_CHUNK) return null

  let idEnd = 18
  for (let i = 2; i < 18; i++) {
    if (bytes[i] === 0) {
      idEnd = i
      break
    }
  }
  const transferId = new TextDecoder().decode(bytes.subarray(2, idEnd))

  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const chunkIndex = dv.getUint32(18, false)
  const totalChunks = dv.getUint32(22, false)
  const payload = bytes.subarray(FRAME_HEADER_LEN)

  return { transferId, chunkIndex, totalChunks, payload }
}

/**
 * Calculates adaptive chunk size according to file magnitude:
 * - < 1 MB: 32 KiB (instant latency)
 * - 1 MB - 10 MB: 64 KiB
 * - 10 MB - 100 MB: 128 KiB
 * - >= 100 MB: 256 KiB (optimal SCTP packet aggregation)
 */
export function calculateAdaptiveChunkSize(fileSize: number): number {
  if (fileSize < 1024 * 1024) return 32 * 1024
  if (fileSize < 10 * 1024 * 1024) return 64 * 1024
  if (fileSize < 100 * 1024 * 1024) return 128 * 1024
  return 256 * 1024
}

export function formatThroughput(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return '0 B/s'
  if (bps < 1024) return `${bps.toFixed(0)} B/s`
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(1)} KB/s`
  return `${(bps / (1024 * 1024)).toFixed(2)} MB/s`
}

export function formatEtaSeconds(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0 sn'
  if (sec < 60) return `${Math.ceil(sec)} sn`
  const m = Math.floor(sec / 60)
  const rem = Math.ceil(sec % 60)
  return `${m} dk ${rem} sn`
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export interface LiveTransferStats {
  transferId: string
  fileName: string
  fileSize: number
  bytesTransferred: number
  percent: number
  currentChunk: number
  totalChunks: number
  speedBps: number
  speedFormatted: string
  etaSeconds: number
  etaFormatted: string
  elapsedSeconds: number
  directDisk: boolean
  isComplete: boolean
  error?: string
}

export interface FileMetaMessage {
  kind: 'file-meta'
  id: string
  name: string
  size: number
  mimeType: string
  totalChunks: number
  chunkSize: number
  sha256?: string | null
  binaryMode?: boolean
}

export interface FileAckMessage {
  kind: 'file-ack'
  id: string
  ok: boolean
  reason?: string
  directDisk?: boolean
}

/**
 * Check if the File System Access API is available in the current browser
 */
export function isDirectDiskSupported(): boolean {
  return typeof window !== 'undefined' && 'showSaveFilePicker' in window
}

/**
 * On-The-Fly File Sender
 * Reads and streams file chunks incrementally without holding the whole file in RAM.
 */
export class OnTheFlyFileSender {
  private isCancelled = false
  private isPaused = false

  constructor(
    private readonly connectionId: string,
    private readonly manager: typeof webrtcManager = webrtcManager
  ) {}

  public cancel() {
    this.isCancelled = true
  }

  public pause() {
    this.isPaused = true
  }

  public resume() {
    this.isPaused = false
  }

  /**
   * Streams a file on the fly over WebRTC DataChannel.
   */
  public async send(
    file: File,
    onProgress: (stats: LiveTransferStats) => void
  ): Promise<void> {
    const transferId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`.slice(0, 16)
    const chunkSize = calculateAdaptiveChunkSize(file.size)
    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize))
    const startTime = Date.now()

    let bytesTransferred = 0
    let lastTime = startTime
    let lastBytes = 0
    let smoothedSpeedBps = 0

    // Send metadata announcement
    const meta: FileMetaMessage = {
      kind: 'file-meta',
      id: transferId,
      name: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      totalChunks,
      chunkSize,
      binaryMode: true,
    }

    const sentMeta = this.manager.sendMessage(this.connectionId, JSON.stringify(meta))
    if (!sentMeta) {
      throw new Error('DataChannel kapalı veya hazır değil')
    }

    // Set up confirmation Promise for receiver ack
    const ackPromise = new Promise<FileAckMessage>((resolve, reject) => {
      const timeoutMs = 30000 + Math.ceil(file.size / (1024 * 1024)) * 1500
      const timer = setTimeout(() => {
        window.removeEventListener('webrtc-message', listener)
        reject(new Error('Alıcıdan doğrulama onayı (ACK) zaman aşımına uğradı'))
      }, timeoutMs)

      const listener = (event: Event) => {
        const detail = (event as CustomEvent).detail ?? {}
        if (detail.connectionId !== this.connectionId) return
        if (typeof detail.message !== 'string') return
        try {
          const parsed = JSON.parse(detail.message)
          if (parsed && parsed.kind === 'file-ack' && parsed.id === transferId) {
            clearTimeout(timer)
            window.removeEventListener('webrtc-message', listener)
            if (parsed.ok) {
              resolve(parsed as FileAckMessage)
            } else {
              reject(new Error(`Alıcı dosyayı reddetti: ${parsed.reason || 'Bilinmeyen hata'}`))
            }
          }
        } catch {
          // Non-JSON message ignored
        }
      }

      window.addEventListener('webrtc-message', listener)
    })

    // Slice and stream chunks ON THE FLY
    for (let index = 0; index < totalChunks; index++) {
      if (this.isCancelled) {
        this.manager.sendMessage(this.connectionId, JSON.stringify({ kind: 'file-cancel', id: transferId }))
        throw new Error('Dosya aktarımı kullanıcı tarafından iptal edildi')
      }

      while (this.isPaused && !this.isCancelled) {
        await new Promise((r) => setTimeout(r, 100))
      }

      const offset = index * chunkSize
      const end = Math.min(offset + chunkSize, file.size)

      // On-the-fly slice: Only the current chunk is read from disk into RAM
      const sliceBlob = file.slice(offset, end)
      const chunkBuffer = await sliceBlob.arrayBuffer()
      const payload = new Uint8Array(chunkBuffer)

      // Backpressure: Wait for SCTP send buffer to drain below threshold
      await this.manager.waitForBufferDrain(this.connectionId, 256 * 1024)

      // Transmit raw binary frame
      const frame = encodeChunkFrame(transferId, index, totalChunks, payload)
      const success = this.manager.sendBinary(this.connectionId, frame)
      if (!success) {
        throw new Error(`Parça ${index + 1}/${totalChunks} gönderilemedi`)
      }

      bytesTransferred += payload.length

      // Compute throughput & ETA
      const now = Date.now()
      const dt = (now - lastTime) / 1000
      if (dt >= 0.2 || index === totalChunks - 1) {
        const instantSpeed = (bytesTransferred - lastBytes) / Math.max(0.001, dt)
        smoothedSpeedBps = smoothedSpeedBps === 0 ? instantSpeed : 0.7 * smoothedSpeedBps + 0.3 * instantSpeed
        lastTime = now
        lastBytes = bytesTransferred
      }

      const remainingBytes = file.size - bytesTransferred
      const etaSec = smoothedSpeedBps > 0 ? remainingBytes / smoothedSpeedBps : 0
      const percent = Math.min(99.5, Math.round((bytesTransferred / file.size) * 1000) / 10)

      onProgress({
        transferId,
        fileName: file.name,
        fileSize: file.size,
        bytesTransferred,
        percent,
        currentChunk: index + 1,
        totalChunks,
        speedBps: smoothedSpeedBps,
        speedFormatted: formatThroughput(smoothedSpeedBps),
        etaSeconds: etaSec,
        etaFormatted: formatEtaSeconds(etaSec),
        elapsedSeconds: Math.round((now - startTime) / 1000),
        directDisk: false,
        isComplete: false,
      })
    }

    // Inform receiver that all chunks have been streamed
    this.manager.sendMessage(this.connectionId, JSON.stringify({ kind: 'file-complete', id: transferId }))

    // Await receiver verification
    const ack = await ackPromise

    // Final completion stats
    const totalTimeSec = Math.max(0.001, (Date.now() - startTime) / 1000)
    const finalAvgSpeed = file.size / totalTimeSec
    onProgress({
      transferId,
      fileName: file.name,
      fileSize: file.size,
      bytesTransferred: file.size,
      percent: 100,
      currentChunk: totalChunks,
      totalChunks,
      speedBps: finalAvgSpeed,
      speedFormatted: formatThroughput(finalAvgSpeed),
      etaSeconds: 0,
      etaFormatted: 'Tamamlandı',
      elapsedSeconds: Math.round(totalTimeSec),
      directDisk: Boolean(ack.directDisk),
      isComplete: true,
    })
  }
}

export interface IncomingTransfer {
  meta: FileMetaMessage
  receivedChunks: number
  totalChunks: number
  bytesReceived: number
  startTime: number
  lastTime: number
  lastBytes: number
  speedBps: number
  writableStream?: FileSystemWritableFileStream | null
  inMemoryParts?: (Uint8Array | null)[]
  directDisk: boolean
}

/**
 * On-The-Fly File Receiver
 * Handles incoming binary chunks, writing directly to disk or progressive memory chunks.
 */
export class OnTheFlyFileReceiver {
  private incoming = new Map<string, IncomingTransfer>()
  private directDiskEnabled = false

  constructor(
    private readonly connectionId: string,
    private readonly manager: typeof webrtcManager = webrtcManager,
    options?: { enableDirectDisk?: boolean }
  ) {
    this.directDiskEnabled = options?.enableDirectDisk ?? false
  }

  public setDirectDiskEnabled(enabled: boolean) {
    this.directDiskEnabled = enabled
  }

  public isDirectDiskActive(): boolean {
    return this.directDiskEnabled && isDirectDiskSupported()
  }

  /**
   * Initializes incoming transfer session on metadata arrival
   */
  public async handleMetadata(
    meta: FileMetaMessage,
    onDirectDiskPrompt?: (fileName: string) => Promise<FileSystemWritableFileStream | null>
  ): Promise<void> {
    let writable: FileSystemWritableFileStream | null = null
    let useDirect = false

    if (this.isDirectDiskActive() && onDirectDiskPrompt) {
      try {
        writable = await onDirectDiskPrompt(meta.name)
        if (writable) {
          useDirect = true
        }
      } catch (err) {
        console.warn('Direct-to-Disk prompt skipped or denied; falling back to progressive in-memory stream', err)
      }
    }

    const record: IncomingTransfer = {
      meta,
      receivedChunks: 0,
      totalChunks: meta.totalChunks,
      bytesReceived: 0,
      startTime: Date.now(),
      lastTime: Date.now(),
      lastBytes: 0,
      speedBps: 0,
      writableStream: writable,
      inMemoryParts: useDirect ? undefined : Array.from({ length: meta.totalChunks }, () => null),
      directDisk: useDirect,
    }

    this.incoming.set(meta.id, record)
  }

  /**
   * Processes an incoming raw binary chunk frame
   */
  public async handleBinaryChunk(
    buffer: ArrayBuffer | Uint8Array,
    onProgress?: (stats: LiveTransferStats) => void
  ): Promise<void> {
    const frame = decodeChunkFrame(buffer)
    if (!frame) return

    const transfer = this.incoming.get(frame.transferId)
    if (!transfer) return

    // Stream directly to disk or buffer in memory
    if (transfer.directDisk && transfer.writableStream) {
      await transfer.writableStream.write({
        type: 'write',
        position: frame.chunkIndex * transfer.meta.chunkSize,
        data: frame.payload,
      })
    } else if (transfer.inMemoryParts) {
      transfer.inMemoryParts[frame.chunkIndex] = frame.payload
    }

    transfer.receivedChunks += 1
    transfer.bytesReceived += frame.payload.length

    // Throughput and ETA calculation
    const now = Date.now()
    const dt = (now - transfer.lastTime) / 1000
    if (dt >= 0.2 || transfer.receivedChunks === transfer.totalChunks) {
      const instantSpeed = (transfer.bytesReceived - transfer.lastBytes) / Math.max(0.001, dt)
      transfer.speedBps = transfer.speedBps === 0 ? instantSpeed : 0.7 * transfer.speedBps + 0.3 * instantSpeed
      transfer.lastTime = now
      transfer.lastBytes = transfer.bytesReceived
    }

    const remainingBytes = transfer.meta.size - transfer.bytesReceived
    const etaSec = transfer.speedBps > 0 ? remainingBytes / transfer.speedBps : 0
    const percent = Math.min(99.9, Math.round((transfer.bytesReceived / transfer.meta.size) * 1000) / 10)

    if (onProgress) {
      onProgress({
        transferId: frame.transferId,
        fileName: transfer.meta.name,
        fileSize: transfer.meta.size,
        bytesTransferred: transfer.bytesReceived,
        percent,
        currentChunk: transfer.receivedChunks,
        totalChunks: transfer.totalChunks,
        speedBps: transfer.speedBps,
        speedFormatted: formatThroughput(transfer.speedBps),
        etaSeconds: etaSec,
        etaFormatted: formatEtaSeconds(etaSec),
        elapsedSeconds: Math.round((now - transfer.startTime) / 1000),
        directDisk: transfer.directDisk,
        isComplete: false,
      })
    }
  }

  /**
   * Finalizes the incoming transfer upon `file-complete` message
   */
  public async finalizeTransfer(
    transferId: string
  ): Promise<{ name: string; size: number; url?: string; directDisk: boolean }> {
    const transfer = this.incoming.get(transferId)
    if (!transfer) {
      throw new Error(`Aktarım bulunamadı: ${transferId}`)
    }

    this.incoming.delete(transferId)

    const reply = (ok: boolean, reason?: string) => {
      try {
        this.manager.sendMessage(
          this.connectionId,
          JSON.stringify({
            kind: 'file-ack',
            id: transferId,
            ok,
            reason,
            directDisk: transfer.directDisk,
          })
        )
      } catch (e) {
        console.warn('Failed to send file-ack', e)
      }
    }

    // Direct-to-Disk completion
    if (transfer.directDisk && transfer.writableStream) {
      try {
        await transfer.writableStream.close()
        reply(true)
        return {
          name: transfer.meta.name,
          size: transfer.bytesReceived,
          directDisk: true,
        }
      } catch (err: any) {
        reply(false, `Diske yazma hatası: ${err.message}`)
        throw err
      }
    }

    // In-memory progressive Blob reconstruction
    if (!transfer.inMemoryParts || transfer.inMemoryParts.includes(null)) {
      reply(false, 'Eksik parçalar tespit edildi')
      throw new Error('Eksik parçalar tespit edildi')
    }

    if (transfer.bytesReceived !== transfer.meta.size) {
      reply(false, `Boyut uyuşmazlığı: beklenen ${transfer.meta.size}, alınan ${transfer.bytesReceived}`)
      throw new Error('Boyut uyuşmazlığı')
    }

    // Create Blob directly from binary chunk array without duplicate buffer copies
    const blob = new Blob(transfer.inMemoryParts as Uint8Array[], {
      type: transfer.meta.mimeType || 'application/octet-stream',
    })
    const url = URL.createObjectURL(blob)

    reply(true)

    return {
      name: transfer.meta.name,
      size: transfer.bytesReceived,
      url,
      directDisk: false,
    }
  }
}
